import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AuthFooter from '@/common/components/AuthFooter/AuthFooter';
import emailSentImg from '@/common/assets/email-sent.svg';
import AuthHeader from '@/common/components/AuthHeader/AuthHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ResetPassword as ResetPasswordForm } from '@/common/containers/Forms/index';
import { useRequestPasswordResetMutation } from '@/entities/user';
import resetPasswordStyles from '@/modules/reset-password/reset-password.styles';
import { useTranslation } from 'react-i18next';

const ResetPasswordModule = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [userEmail, setUserEmail] = useState('');
  const requestPasswordResetMutation = useRequestPasswordResetMutation();

  const TEXT = useMemo(() => {
    return [
      {
        title: t('Reset password'),
        label: t(
          'Please enter email address associated with your account an we will send you an email with instructions to reset your password.'
        ),
      },
      {
        title: t('Instructions sent'),
        label: (
          <span>
            {t('Please follow the instructions we’ve sent to your email')}{' '}
            <b>{userEmail}</b>
          </span>
        ),
      },
    ][userEmail ? 1 : 0];
  }, [t, userEmail]);

  const onSubmit = useCallback(
    async ({ email }) => {
      try {
        await requestPasswordResetMutation.mutateAsync({ email });
        setUserEmail(email);
      } catch (e) {
        console.log(e);
      }
    },
    [requestPasswordResetMutation]
  );

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  return (
    <Box component="section" sx={resetPasswordStyles.container}>
      <AuthHeader />
      <Button
        variant="text"
        size="medium"
        onClick={goBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          width: 'fit-content',
          ml: 6,
          mt: 6,
        }}
      >
        {t('Back to login')}
      </Button>
      <Box sx={resetPasswordStyles.formWrapper}>
        {userEmail && (
          <Box
            component="img"
            sx={resetPasswordStyles.successIcon}
            src={emailSentImg}
            alt={t('Instructions sent')}
          />
        )}
        <Typography variant="h2" textAlign="center">
          {TEXT.title}
        </Typography>
        <Typography variant="body1" sx={resetPasswordStyles.description}>
          {TEXT.label}
        </Typography>
        {!userEmail ? (
          <ResetPasswordForm
            onSubmit={onSubmit}
            isPending={requestPasswordResetMutation.isPending}
            sx={resetPasswordStyles.form}
          />
        ) : (
          <Typography sx={resetPasswordStyles.warning}>
            {t('Did not receive an email?')}
            <br />
            {t('Check your spam folder, or')}{' '}
            <Box
              component="span"
              sx={resetPasswordStyles.inlineLink}
              onClick={() => setUserEmail('')}
            >
              {t('Try another email')}
            </Box>
          </Typography>
        )}
      </Box>
      <AuthFooter />
    </Box>
  );
};

export default ResetPasswordModule;
